import { FlexBox, Page, SimplePagination } from '@dixa/lib-shared-habla';
import produce from 'immer';
import { ampli } from 'lib-shared-tracking';
import { useCallback, useRef } from 'react';
import { useMount, useUnmount } from 'react-use';

import { dashboardApi } from 'apis/dashboard';
import { websocketActions } from 'app/middleware/websocket/actions/websocket';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSearchParams } from 'utils/url/useAppSearchParams';
import { DragMoveEvent } from 'utils/useOrderedDragDrop';
import { useServerStorage } from 'utils/useServerStorage';

import { DashboardError } from '../../components/error/dashboardError';
import { DashboardHeader } from '../../components/header/dashboardHeader';
import { dashboardActions } from '../../dashboard.actions';
import rootStyles from '../../dashboard.view.module.scss';

import { QueueDashboardControls } from './components/controls/queueDashboardControls';
import { QueueDashboardEmpty } from './components/empty/queueDashboardEmpty';
import { QueueDashboardLoading } from './components/loading/queueDashboardLoading';
import { QueueDashboardQueues } from './components/queues/queueDashboardQueues';
import { useQueueDashboardFavorite } from './utils/useQueueDashboardFavorite';
import { useQueueDashboardQueues } from './utils/useQueueDashboardQueues';

export const QueueDashboardView = (): JSX.Element => {
  const dispatch = useAppDispatch();
  const notify = useNotifications();
  const contentRef = useRef<HTMLDivElement>(null);
  const storage = useServerStorage('user');
  const { layout = 'default' } = storage.data.dashboard ?? {};
  const { params, setParam } = useAppSearchParams('/dashboard/queues');

  const [
    ,
    {
      results: queues,
      meta,
      isUninitialized,
      isFetching,
      isError,
      hasNextPage,
      hasPreviousPage,
      updateResults,
    },
  ] = useQueueDashboardQueues();

  const { favorite, isOwnerOfFavorite } = useQueueDashboardFavorite();
  const [updateFavorite] = dashboardApi.useUpdateDashboardFavoriteMutation();

  useMount(() => {
    ampli.dashboardViewed({ version: 'new', layout });

    dispatch(
      websocketActions.request({
        type: 'connection:subscribe',
        params: {
          type: 'real_time_dashboard',
          dashboard_type: 'queue',
        },
      }),
    );
  });

  useUnmount(() => {
    dispatch(
      websocketActions.request({
        type: 'connection:unsubscribe',
        params: {
          type: 'real_time_dashboard',
          dashboard_type: 'queue',
        },
      }),
    );

    dispatch(
      dashboardActions.setLastVisitedUrl({
        path: '/dashboard/queues',
        params: {
          search: params,
        },
      }),
    );
  });

  // Ordering queues is enabled when viewing a favorite owned by the current user, as well as
  // when in "custom" mode where the user manually selects queues to create a new favorite list.
  const isOrderingEnabled =
    queues.length > 1 &&
    (params.type === 'custom' ||
      (params.type === 'favourite' && isOwnerOfFavorite));

  /**
   * Handles changing the order of the queues. The list is optimistically
   * reordered in place. In "custom" mode, the `queue` param in the URL is
   * updated. In "favorite" mode, the favorite is patched via the API.
   */
  const handleOrderChange = useCallback(
    async ({ dragIndex, hoverIndex }: DragMoveEvent) => {
      if (!isOrderingEnabled) return;

      try {
        const newQueueIds = produce(
          queues.map(({ id }) => id),
          (draft) => {
            const [movedQueueId] = draft.splice(dragIndex, 1);
            draft.splice(hoverIndex, 0, movedQueueId);
          },
        );

        // Optimistically reorder the local results.
        updateResults((draft) => {
          const [moved] = draft.splice(dragIndex, 1);
          draft.splice(hoverIndex, 0, moved);
        });

        if (params.type === 'custom') {
          setParam('queue', newQueueIds);
        } else if (params.type === 'favourite' && favorite) {
          await updateFavorite({
            id: favorite.id,
            favorite: {
              params: {
                queue_ids: newQueueIds,
              },
            },
          });

          ampli.dashboardFavoriteQueuesReordered();
        }
      } catch (error) {
        notify(
          {
            title: 'Error while reordering the queues.',
          },
          NotificationType.Error,
        );
      }
    },
    [
      updateResults,
      favorite,
      isOrderingEnabled,
      notify,
      params.type,
      queues,
      setParam,
      updateFavorite,
    ],
  );

  // Pagination is 1-based in the URL params and 0-based in the API
  const page = (meta?.page ?? 0) + 1;

  const handleNextPage = useCallback(() => {
    if (!hasNextPage) return;
    setParam('page', `${page + 1}`);
    ampli.dashboardNextPageClicked();
  }, [hasNextPage, page, setParam]);

  const handlePreviousPage = useCallback(() => {
    if (!hasPreviousPage) return;
    setParam('page', page - 1 > 1 ? `${page - 1}` : undefined);
    ampli.dashboardPreviousPageClicked();
  }, [hasPreviousPage, page, setParam]);

  const renderContent = (): JSX.Element => {
    if (isUninitialized || isFetching) {
      return (
        <QueueDashboardLoading
          contentHeight={contentRef.current?.clientHeight}
        />
      );
    }

    if (isError) {
      return <DashboardError />;
    }

    if (queues.length === 0) {
      return <QueueDashboardEmpty />;
    }

    return (
      <>
        <QueueDashboardQueues
          queues={queues}
          onOrderChange={isOrderingEnabled ? handleOrderChange : undefined}
        />

        {(hasNextPage || hasPreviousPage) && (
          <SimplePagination
            previousButtonClick={handlePreviousPage}
            previousButtonDisabled={!hasPreviousPage}
            nextButtonClick={handleNextPage}
            nextButtonDisabled={!hasNextPage}
          />
        )}
      </>
    );
  };

  return (
    <Page>
      <Page.Head
        className={rootStyles.pageHead}
        title={<DashboardHeader dashboard="queue" />}
        controls={<QueueDashboardControls />}
      />

      <Page.Body ref={contentRef}>
        <Page.Section>
          <FlexBox flexDirection="column" gap="300" height="100%">
            {renderContent()}
          </FlexBox>
        </Page.Section>
      </Page.Body>
    </Page>
  );
};
